import { type ExchangeSummary, type SvNotice, postConsent } from '@/api/capture';
import { SvApiError } from '@/api/errors';
import { warmVoices } from '@/capture/guidance/speech';
import { noticeCues } from '@/capture/guidance/guidanceCopy';
import { guidancePlayer, useGuidance } from '@/capture/guidance/guidanceStore';
import { noticeSections } from '@/capture/guidance/noticeSections';
import { rich, useT } from '@/i18n/runtime';
import { useCaptureStore } from '@/store/captureStore';
import type { TenantConfig } from '@/theme/tenant';
import { type KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { AppBar } from './components/AppBar';
import { Button } from './components/Button';
import { GuidanceNote } from './components/GuidanceControls';
import { Icon, type IconName } from './components/Icon';
import { NoticeBody } from './components/NoticeBody';
import { isDwelling, isSharedAccommodation } from './premisesKind';
import { useHelpStep } from './help/HelpContext';

/**
 * The granular DPDP consent ceremony (FSD §14, PRIM-CONSENT) — forked in shape from
 * vCIP_droom_4.0/src/flow/steps/Consent.tsx, with per-purpose grants:
 *   premises video · persons in frame · geolocation · identity document · onward sharing.
 *
 * The client's `mandatory` flags are a UX HINT so the representative is told up-front which items
 * the verification cannot run without; the SERVER is the authority — a 409
 * SV_CONSENT_MANDATORY_DECLINED names the purpose and renders the same honest block. The bystander
 * minimisation prompt and the residence variant are part of the notice, not an afterthought.
 * Nothing is recorded until POST /consent returns `recordedAt`.
 */
/** The client's LEGACY purpose vocabulary — used only when an older be sends no `consentPurposes`. */
export type PurposeKey = 'PREMISES_VIDEO' | 'PERSONS_IN_FRAME' | 'GEOLOCATION' | 'IDENTITY_DOCUMENT' | 'ONWARD_SHARING';

const ICONS: Readonly<Record<string, IconName>> = {
  PREMISES_VIDEO: 'cam',
  PERSONS_IN_FRAME: 'users',
  GEOLOCATION: 'mappin',
  LOCATION: 'mappin',
  IDENTITY_DOCUMENT: 'doc',
  REGISTRY_CROSS_MATCH: 'shield-check',
  DATA_RETENTION: 'lock',
  ONWARD_SHARING: 'shield-check',
  // SV-5-2 second round: the three live-call purposes the v2 notice adds when the live leg applies
  LIVE_VIDEO: 'cam',
  LIVE_IDENTITY_CHECK: 'shield-check',
  LIVE_CUSTODY_RETENTION: 'lock',
};

export const PURPOSES: readonly { key: PurposeKey; icon: IconName }[] = [
  { key: 'PREMISES_VIDEO', icon: 'cam' },
  { key: 'PERSONS_IN_FRAME', icon: 'users' },
  { key: 'GEOLOCATION', icon: 'mappin' },
  { key: 'IDENTITY_DOCUMENT', icon: 'doc' },
  { key: 'ONWARD_SHARING', icon: 'shield-check' },
];

export interface PurposeRow {
  readonly key: string;
  readonly icon: IconName;
  readonly mandatory: boolean;
  /** True on the rows the be flagged `liveLeg` — rendered in their own group ("for the live video call"). */
  readonly liveLeg: boolean;
}

/**
 * The rows the ceremony shows. The SERVER's `consentPurposes` (the pack's vocabulary + mandatory flags) is the
 * authority whenever present — the be refuses a POST that omits a mandatory key (409 SV_CONSENT_MANDATORY_DECLINED),
 * so the client must render and send the server's keys, not its own. The legacy five are the fallback for an older be.
 */
export function purposeRows(summary: ExchangeSummary): readonly PurposeRow[] {
  if (summary.consentPurposes && summary.consentPurposes.length > 0) {
    return summary.consentPurposes.map((p) => ({ key: p.key, icon: ICONS[p.key] ?? 'shield-check', mandatory: p.mandatory, liveLeg: p.liveLeg === true }));
  }
  const legacy = mandatoryHint(summary);
  return PURPOSES.map((p) => ({ key: p.key, icon: p.icon, mandatory: legacy.has(p.key), liveLeg: false }));
}

/** The client-side mandatory hint (legacy). `IDENTITY_DOCUMENT` is required only when the capture list asks for it. */
export function mandatoryHint(summary: ExchangeSummary): ReadonlySet<PurposeKey> {
  const wantsId = summary.captureList.some((x) => /identity document|\bID\b/i.test(x));
  return new Set<PurposeKey>(['PREMISES_VIDEO', 'PERSONS_IN_FRAME', 'GEOLOCATION', ...(wantsId ? (['IDENTITY_DOCUMENT'] as PurposeKey[]) : [])]);
}

/**
 * The version a customer reads on the consent card: `SITE_VERIFICATION_CUSTOMER@v2` → `2` (FSD-18 D7). The full id is
 * an internal key; it still travels, untouched, as the consent payload's `noticeVersion`.
 *
 * The customer reads the NUMBER only. Anything after it (`@v2-UNRATIFIED`) is a pack status, an internal word, and
 * DEC-073 keeps status vocabulary off customer surfaces in both directions; the consent screen's own test pins "no
 * UNRATIFIED on screen". So `@v2-UNRATIFIED` → `2`, `@v2.1-rc` → `2.1`. An id with no number yields `null`: the card then
 * shows no version line, rather than any internal word (no word list to keep in step: only digits ever pass).
 */
export function noticeVersionLabel(noticeVersion: string): string | null {
  const version = noticeVersion.includes('@') ? noticeVersion.slice(noticeVersion.lastIndexOf('@') + 1) : noticeVersion;
  return /^v?(\d+(?:\.\d+)*)/i.exec(version.trim())?.[1] ?? null;
}

/** The notice of the given role from the issuance payload — the AGENT notice is never a representative-surface notice. */
export function noticeOf(summary: ExchangeSummary, role: 'CUSTOMER' | 'BYSTANDER_PROMPT'): SvNotice | null {
  return summary.notices?.find((n) => n.role === role) ?? null;
}

type Phase = { kind: 'ready' } | { kind: 'posting' } | { kind: 'recorded' } | { kind: 'declined'; purpose: string } | { kind: 'error' };

/**
 * VS-08: the note beside a notice served in another language than the one that controls. English controls unless the
 * tenant elected the text it ratified (the server's `controllingLocale`); a language the deck cannot name falls back to
 * the English note rather than claiming a wrong reference text.
 */
export function controllingNote(t: (key: string) => string, controllingLocale: string): string {
  if (controllingLocale && controllingLocale !== 'en') {
    const key = `sv.consent.noticeControlling.${controllingLocale}`;
    const named = t(key);
    if (named !== key) return named;
  }
  return t('sv.consent.noticeControlling');
}

export function ConsentStep({ tenant, summary, onDone }: { tenant: TenantConfig; summary: ExchangeSummary; onDone: () => void }): JSX.Element {
  const { t, locale } = useT();
  const setConsent = useCaptureStore((s) => s.setConsent);
  const rows = useMemo(() => purposeRows(summary), [summary]);
  const baseRows = useMemo(() => rows.filter((r) => !r.liveLeg), [rows]);
  const liveRows = useMemo(() => rows.filter((r) => r.liveLeg), [rows]);
  const customerNotice = useMemo(() => noticeOf(summary, 'CUSTOMER'), [summary]);
  const bystanderNotice = useMemo(() => noticeOf(summary, 'BYSTANDER_PROMPT'), [summary]);
  const [granted, setGranted] = useState<Record<string, boolean>>(() => Object.fromEntries(rows.map((r) => [r.key, true])));
  const [ticked, setTicked] = useState(false);
  const [phase, setPhase] = useState<Phase>({ kind: 'ready' });
  useHelpStep({ screen: 'consent', tenantName: tenant.name });
  /**
   * SPEC_CAPTURE_UX_R2 §3.2 — the notice, read aloud, SECTION BY SECTION: the same text the customer scrolls, cut
   * at the headings `NoticeBody` renders. It is an accessibility AID and nothing else: it does not constitute or
   * evidence agreement, it never advances the ceremony, and the single consent action stays the "I agree and start"
   * tap (DPDP; the ratified notice text remains the record). With a reviewed asset the asset reads it; with none, the
   * phone's own voice reads the same text (US-03482 G7, 2026-11-03 — it was silent on every phone, iPhone included);
   * only a phone with no voice for the language shows the honest rung-3 note.
   */
  const guidance = useGuidance();
  const sections = useMemo(() => (customerNotice ? noticeSections(customerNotice.body) : []), [customerNotice]);
  const noticeVersion = customerNotice?.noticeVersion ?? summary.noticeVersion;
  // Leaving the screen (or agreeing) stops the reading; nothing keeps speaking over the next step. The player is
  // reached directly here, NOT through `guidance`: the hook's value changes as the player's state changes, and a
  // cleanup keyed on it would cancel the read one render after it started.
  useEffect(() => () => guidancePlayer().cancel(), []);
  // iOS fills the voice list asynchronously — ask for it as the screen opens, so the first tap finds a voice.
  useEffect(() => warmVoices(), []);

  const toggle = (key: string): void => setGranted((g) => ({ ...g, [key]: !g[key] }));
  const onKey = (e: KeyboardEvent<HTMLDivElement>, fn: () => void): void => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      fn();
    }
  };

  const listen = (): void => {
    if (sections.length === 0) return;
    // The notice's own reviewed audio when the be sends it (it does not yet), else the device voice. Called straight
    // from the tap: iOS WebKit only lets speech START inside a gesture (the player primes it on unlock).
    // F9: read in the voice of the language the notice is WRITTEN in (`notice.locale`), not the deck language.
    guidance.playSections(
      noticeCues(sections, locale, noticeVersion, (customerNotice as { audio?: unknown } | null)?.audio, customerNotice?.locale),
    );
  };

  const affirm = async (): Promise<void> => {
    guidance.stop();
    const declined = rows.find((p) => p.mandatory && !granted[p.key]);
    if (declined) {
      setPhase({ kind: 'declined', purpose: declined.key });
      return;
    }
    setPhase({ kind: 'posting' });
    try {
      const res = await postConsent({
        noticeVersion: customerNotice?.noticeVersion ?? summary.noticeVersion,
        purposes: rows.map((p) => ({ key: p.key, granted: granted[p.key] !== false })),
      });
      setConsent(res.recordedAt);
      setPhase({ kind: 'recorded' });
      window.setTimeout(onDone, 600);
    } catch (e) {
      if (e instanceof SvApiError && e.code === 'SV_CONSENT_MANDATORY_DECLINED') {
        const d = e.details as { purposeKey?: unknown } | null;
        const key = typeof d?.purposeKey === 'string' ? d.purposeKey : 'PREMISES_VIDEO';
        setPhase({ kind: 'declined', purpose: key });
        return;
      }
      setPhase({ kind: 'error' });
    }
  };

  if (phase.kind === 'declined') {
    return (
      <div className="app-view">
        <AppBar tenant={tenant} subtitle={t('sv.subtitle')} />
        <div className="app-view center" style={{ paddingTop: 24 }}>
          <div className="center-col" style={{ maxWidth: '34ch' }}>
            <span className="consent-gate-badge"><Icon name="lock" size={28} /></span>
            <div className="h-title sm" data-testid="consent-declined">{t('sv.consent.declined.title')}</div>
            <p className="sub" style={{ marginTop: 8 }}>
              {t('sv.consent.declined.body', { purpose: purposeTitle(t, phase.purpose, tenant.name) })}
            </p>
            <Button style={{ marginTop: 18 }} onClick={() => setPhase({ kind: 'ready' })}>
              {t('sv.consent.declined.review')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const busy = phase.kind === 'posting' || phase.kind === 'recorded';
  // VS-02: the dwelling floor keys on the server's privacy class (an India home, or worker accommodation)
  const isHome = isDwelling(summary);
  const sharedAccommodation = isSharedAccommodation(summary);

  const renderRow = (p: PurposeRow): JSX.Element => {
    const req = p.mandatory;
    const on = granted[p.key] !== false;
    return (
      <div
        className="purpose sv-purpose"
        key={p.key}
        role="checkbox"
        aria-checked={on}
        tabIndex={0}
        data-testid={`purpose-${p.key}`}
        data-mandatory={req ? 'true' : 'false'}
        data-live-leg={p.liveLeg ? 'true' : 'false'}
        onClick={() => toggle(p.key)}
        onKeyDown={(e) => onKey(e, () => toggle(p.key))}
      >
        <span className="pic"><Icon name={p.icon} size={16} /></span>
        <div style={{ flex: 1 }}>
          <div className="pt">
            {purposeTitle(t, p.key, tenant.name)}{' '}
            <span className={`sv-req${req ? ' is-req' : ''}`}>{req ? t('sv.consent.required') : t('sv.consent.optional')}</span>
          </div>
          <div className="pd">{purposeDetail(t, p.key, tenant.name)}</div>
        </div>
        <span className={`sv-switch${on ? ' is-on' : ''}`} aria-hidden="true"><i /></span>
      </div>
    );
  };

  // DEC-073: the number only; no number ⇒ no version line, never an internal status word
  const noticeLabel = noticeVersionLabel(summary.noticeVersion);

  return (
    <div className="app-view">
      <AppBar tenant={tenant} subtitle={t('sv.subtitle')} />
      <div className="stack">
        <div>
          <div className="h-title sm">{t('sv.consent.title')}</div>
          <p className="sub-sm" style={{ marginTop: 6 }}>{t('sv.consent.sub', { tenantName: tenant.name })}</p>
        </div>

        <div className="card notice-summary">
          {noticeLabel !== null ? (
            <div className="notice-head">
              <span style={{ fontWeight: 700, fontSize: 'var(--fs-cap)' }} data-testid="notice-version">{t('sv.consent.notice', { version: noticeLabel })}</span>
            </div>
          ) : null}
          {baseRows.map(renderRow)}
        </div>

        {liveRows.length > 0 ? (
          <div className="card notice-summary" data-testid="consent-live-group">
            <div className="notice-head">
              <span style={{ fontWeight: 700, fontSize: 'var(--fs-cap)' }}><Icon name="cam" size={14} /> {t('sv.consent.live.group')}</span>
            </div>
            {liveRows.map(renderRow)}
          </div>
        ) : null}

        {isHome ? (
          <div className="card pad sv-residence" role="note" data-testid="consent-residence">
            <Icon name="info" size={16} /> {t(sharedAccommodation ? 'sv.consent.dwelling' : 'sv.consent.residence')}
          </div>
        ) : null}

        {customerNotice && sections.length > 0 ? (
          <div className="card pad sv-listen" data-testid="consent-listen-card">
            <button type="button" className="sv-listen-btn" onClick={listen} data-testid="consent-listen" data-sections={sections.length}>
              <Icon name="speaker" size={18} />
              <span>{t('sv.audio.listenNotice')}</span>
            </button>
            {/* The player's OWN cue key, not the screen-registered one: the read is started with `playSections`, which
                never registers a cue — a stop control keyed on the registered cue would never appear. */}
            {guidance.state.playing && (guidance.state.cueKey ?? '').startsWith('NOTICE:') ? (
              <button type="button" className="sv-listen-stop" onClick={() => guidance.stop()} data-testid="consent-listen-stop">
                {t('sv.audio.stop')}
              </button>
            ) : null}
            <p className="sv-listen-aid" role="note" data-testid="consent-listen-aid">
              {t('sv.audio.listenNotice.aid', { action: t('sv.consent.agree') })}
            </p>
            <GuidanceNote testId="consent-listen-unavailable" />
          </div>
        ) : null}

        {customerNotice ? (
          <details className="card pad sv-notice-full" open data-testid="consent-notice-full">
            <summary className="pt">
              <Icon name="doc" size={14} /> {t('sv.consent.noticeFull')}
              {customerNotice.locale !== customerNotice.controllingLocale ? (
                <span className="pd" style={{ marginInlineStart: 6 }} data-testid="consent-notice-controlling">{controllingNote(t, customerNotice.controllingLocale)}</span>
              ) : null}
            </summary>
            <NoticeBody body={customerNotice.body} testId="consent-notice-body" />
          </details>
        ) : null}

        <div className="card pad" data-testid="consent-bystander">
          {bystanderNotice ? (
            <NoticeBody body={bystanderNotice.body} testId="consent-bystander-body" />
          ) : (
            <>
              <div className="pt"><Icon name="users" size={14} /> {t('sv.consent.bystander.t')}</div>
              <div className="pd" style={{ marginTop: 4 }}>{t('sv.consent.bystander.d')}</div>
            </>
          )}
        </div>

        <p className="sub-sm">{t('sv.consent.retention')}</p>

        {/* SPEC_CAPTURE_UX_R2 §8 #2 (companion mode): a helper may hold the phone — the bystander rule still holds for them */}
        <p className="sv-companion" role="note" data-testid="consent-companion">
          <Icon name="users" size={14} /> <span>{t('sv.consent.companion')}</span>
        </p>

        <div
          className="consent-toggle"
          data-on={ticked ? 'true' : 'false'}
          role="checkbox"
          aria-checked={ticked}
          tabIndex={0}
          data-testid="consent-affirm"
          onClick={() => setTicked((v) => !v)}
          onKeyDown={(e) => onKey(e, () => setTicked((v) => !v))}
        >
          <span className="box"><Icon name="check" size={14} /></span>
          <span className="ct">{rich(t('sv.consent.affirm', { tenantName: tenant.name }))}</span>
        </div>

        {phase.kind === 'error' ? (
          <div className="consent-note mono" role="alert">{t('sv.consent.postfail')}</div>
        ) : null}
      </div>

      <div className="spacer" />
      <div className="stack-sm" style={{ marginTop: 16 }}>
        <Button onClick={() => void affirm()} disabled={!ticked || busy} data-testid="consent-agree">
          {phase.kind === 'recorded' ? t('sv.consent.recorded') : phase.kind === 'posting' ? t('sv.consent.recording') : t('sv.consent.agree')}
        </Button>
      </div>
    </div>
  );
}

/** Deck title for a purpose key; an unknown (newer-pack) key falls back to a readable form of the key itself. */
function purposeTitle(t: (k: string, v?: Record<string, string>) => string, key: string, tenantName: string): string {
  const k = `sv.consent.purpose.${key}.t`;
  const v = t(k, { tenantName });
  return v === k ? key.toLowerCase().replace(/_/g, ' ') : v;
}

function purposeDetail(t: (k: string, v?: Record<string, string>) => string, key: string, tenantName: string): string {
  const k = `sv.consent.purpose.${key}.d`;
  const v = t(k, { tenantName });
  return v === k ? '' : v;
}
